import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Btn, Input, PhaseLogo } from "../ui/index.jsx";
// Sprint 26-1: UTM 추적 시스템
import { attachUtmToBody, getStoredUtm } from "../../services/utmService.js";
import { trackSignup } from "../../services/metaPixel.js";

export default function Login({ onLogin, onBack, isSignup: initSignup = false }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [signup, setSignup] = useState(initSignup);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  const SUPABASE_URL = "https://bakfxfsuxxbirgvfzhkn.supabase.co";
  const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJha2Z4ZnN1eHhiaXJndmZ6aGtuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk0MDcwMjIsImV4cCI6MjA1NDk4MzAyMn0.D2q-1gOsjPOi5RqMEN5FxEXJt2r_jPBkxDyPk4MYz38";

  const socialLogin = (provider) => {
    const redirectTo = window.location.origin;
    // iOS Safari 호환: setTimeout 없이 동기적으로 리다이렉트
    // (사용자 제스처와 동일 콜스택에서 실행되어야 차단 안 됨)
    try {
      window.location.assign(`${SUPABASE_URL}/auth/v1/authorize?provider=${provider}&redirect_to=${redirectTo}`);
    } catch {
      window.location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=${provider}&redirect_to=${redirectTo}`;
    }
  };

  // Check for social login callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get("access_token");
      if (token) {
        window.location.hash = "";
        // Sprint 26-5: 소셜 로그인 UTM 저장 (localStorage에 살아있는 UTM을 백엔드로)
        // 이메일 가입은 /signup에서 처리되지만 소셜 로그인은 이 경로를 안 타므로 별도 전송.
        // 백엔드가 "이미 UTM 있으면 안 덮어씀"으로 첫 유입 보존 + 기존 로그인 중복 방지.
        try {
          const utm = getStoredUtm();
          if (utm && utm.utm_source && utm.utm_source !== "direct") {
            api("/auth/update-utm", {
              method: "POST",
              body: JSON.stringify(attachUtmToBody({})),
            }, token).catch(() => {});
            // Meta Pixel Lead (소셜 가입도 추적)
            try { trackSignup(); } catch {}
          }
        } catch (e) {
          console.error("[UTM-SOCIAL] 전송 실패:", e);
        }
        onLogin(token, null);
      }
    }
  }, []);

  const submit = async () => {
    if (!email || !pw) return setErr("이메일과 비밀번호를 입력하세요");
    if (signup && (!agreeTerms || !agreePrivacy)) return setErr("필수 약관에 동의해주세요");
    setErr(""); setLoading(true);
    try {
      // ★ Sprint 26-1: 가입 시 UTM 자동 첨부
      const baseBody = { email, password: pw };
      const body = signup ? attachUtmToBody(baseBody) : baseBody;

      const d = await api(signup ? "/auth/signup" : "/auth/login", {
        method: "POST",
        body: JSON.stringify(body),
      });

      // ★ Sprint 26-1: 가입 성공 시 Meta Pixel Lead 이벤트
      if (signup) {
        try {
          trackSignup();
        } catch (e) {
          console.error("[META PIXEL] Lead 이벤트 실패:", e);
        }
      }

      if (d.session?.access_token) onLogin(d.session.access_token, d.user);
      else if (signup) { setSignup(false); setErr("가입 완료! 로그인하세요"); }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const resetPassword = async () => {
    if (!email) return setErr("비밀번호를 재설정할 이메일을 입력하세요");
    setErr(""); setLoading(true);
    try {
      await api("/auth/reset-password", { method: "POST", body: JSON.stringify({ email }) });
      setResetSent(true);
      setErr("");
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const socialBtnStyle = (bg, hover) => ({
    width: "100%", padding: "12px 16px", borderRadius: 10, border: "none",
    fontSize: 14, fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    background: bg, color: bg === "#FEE500" ? "#191919" : "#fff",
    transition: "opacity 0.2s",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Pretendard', sans-serif" }}>
      <link href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css" rel="stylesheet" />
      <div style={{ width: 400, padding: 44 }}>
        {onBack && <div style={{ fontSize: 13, color: T.textDim, cursor: "pointer", marginBottom: 24 }} onClick={onBack}>← 돌아가기</div>}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <PhaseLogo size={36} />
          </div>
          <div style={{ fontSize: 14, color: T.textDim, marginTop: 8 }}>내 종목의 리스크를 정리해드려요</div>
        </div>

        {/* Social Login Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          <button style={socialBtnStyle("#fff")} onClick={() => socialLogin("google")}
            onTouchEnd={e => { e.preventDefault(); socialLogin("google"); }}>
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.9 33.1 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.2-2.7-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.3 15.5 18.8 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.4 0-9.9-3.6-11.3-8.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.7 39.5 44 34 44 24c0-1.3-.2-2.7-.4-3.9z"/></svg>
            <span style={{ color: "#333" }}>Google로 시작하기</span>
          </button>
          <button style={socialBtnStyle("#FEE500")} onClick={() => socialLogin("kakao")}
            onTouchEnd={e => { e.preventDefault(); socialLogin("kakao"); }}>
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#191919" d="M12 3C6.48 3 2 6.44 2 10.64c0 2.72 1.8 5.1 4.5 6.44-.2.72-.72 2.6-.82 3 0 0-.02.16.08.22.1.06.22.02.22.02.3-.04 3.44-2.26 3.98-2.64.64.1 1.32.16 2.04.16 5.52 0 10-3.44 10-7.68S17.52 3 12 3z"/></svg>
            <span>카카오로 시작하기</span>
          </button>
        </div>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <span style={{ fontSize: 12, color: T.textMuted }}>또는 이메일로</span>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {/* Email Login / Password Reset */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input placeholder="이메일" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && (resetMode ? resetPassword() : submit())} />
          {!resetMode && (
            <Input placeholder="비밀번호" type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} />
          )}

          {/* 회원가입 시 약관 동의 */}
          {signup && !resetMode && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 0" }}>
              {/* 전체 동의 */}
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "10px 12px", background: T.card || T.bg, borderRadius: 10, border: `1px solid ${agreeTerms && agreePrivacy ? T.accent + "40" : T.border}` }}>
                <input type="checkbox" checked={agreeTerms && agreePrivacy && agreeMarketing} onChange={e => { setAgreeTerms(e.target.checked); setAgreePrivacy(e.target.checked); setAgreeMarketing(e.target.checked); }} style={{ width: 18, height: 18, accentColor: T.accent, cursor: "pointer" }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>전체 동의</span>
              </label>

              <div style={{ paddingLeft: 4, display: "flex", flexDirection: "column", gap: 8 }}>
                {/* 이용약관 (필수) */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.accent, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, color: T.text }}>
                    <span style={{ color: T.accent, fontWeight: 600 }}>[필수]</span> 이용약관 동의
                  </span>
                  <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }} onClick={e => { e.preventDefault(); window.open("/terms.html", "_blank"); }}>보기</span>
                </label>

                {/* 개인정보 수집·이용 (필수) */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={agreePrivacy} onChange={e => setAgreePrivacy(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.accent, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, color: T.text }}>
                    <span style={{ color: T.accent, fontWeight: 600 }}>[필수]</span> 개인정보 수집·이용 동의
                  </span>
                  <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }} onClick={e => { e.preventDefault(); window.open("/privacy.html", "_blank"); }}>보기</span>
                </label>

                {/* 마케팅 수신 (선택) */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={agreeMarketing} onChange={e => setAgreeMarketing(e.target.checked)} style={{ width: 16, height: 16, accentColor: T.accent, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, color: T.textDim }}>
                    <span style={{ color: T.textMuted, fontWeight: 600 }}>[선택]</span> 마케팅 정보 수신 동의
                  </span>
                </label>
              </div>
            </div>
          )}
          {err && <div style={{ fontSize: 13, color: T.red, textAlign: "center" }}>{err}</div>}
          {resetMode && resetSent && (
            <div style={{ fontSize: 13, color: T.green || "#22c55e", textAlign: "center", lineHeight: 1.6 }}>
              비밀번호 재설정 링크를 보냈습니다.<br />이메일을 확인해주세요.
            </div>
          )}
          {resetMode ? (
            <>
              {!resetSent && (
                <Btn variant="primary" style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: 15 }} onClick={resetPassword} disabled={loading}>
                  {loading ? "처리 중..." : "재설정 링크 보내기"}
                </Btn>
              )}
              <div style={{ textAlign: "center", fontSize: 13, color: T.textDim, cursor: "pointer" }} onClick={() => { setResetMode(false); setResetSent(false); setErr(""); }}>
                ← 로그인으로 돌아가기
              </div>
            </>
          ) : (
            <>
              <Btn variant="primary" style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: 15 }} onClick={submit} disabled={loading}>{loading ? "처리 중..." : signup ? "가입하기" : "로그인"}</Btn>
              {!signup && (
                <div style={{ textAlign: "center", fontSize: 13, color: T.textDim, cursor: "pointer" }} onClick={() => { setResetMode(true); setErr(""); }}>
                  비밀번호를 잊으셨나요?
                </div>
              )}
              <div style={{ textAlign: "center", fontSize: 13, color: T.textDim, cursor: "pointer" }} onClick={() => { setSignup(!signup); setErr(""); setAgreeTerms(false); setAgreePrivacy(false); setAgreeMarketing(false); }}>
                {signup ? "이미 계정이 있나요? → 로그인" : "계정이 없나요? → 회원가입"}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
